// src/admin/hooks/useCollectionsQueue.ts
// Reads the `collections_queue` view (migration 15 from sub-project B) and
// decorates each row with a synthetic `bucket` field so the UI can group
// cases by days-overdue without the caller having to re-derive the mapping.
//
// Schema realities that differ from the plan:
//   - The view has no `bucket` column — it's derived here from `days_overdue`.
//   - Balance column is `outstanding_ngwee` (not `balance_owed_ngwee`).
//   - Loan status column is `status` (not `loan_status`).
//   - No `borrower_id` FK metadata in generated types, so embedded selects
//     aren't typed — but this view already flattens profile info into
//     `borrower_name` / `borrower_phone`, so no join is needed.
//   - `collection_outcome` enum is the sub-project-B shipped set:
//     no_answer | reached_promise | reached_disputed | reached_refused
//     | wrong_number | left_message. Earlier plan draft named a different
//     set — the UI labels map the shipped enum.
//
// Bucket boundaries match the delinquency escalation schedule in CLAUDE.md:
//   0_14d  — overdue but not yet delinquent (early reminders)
//   14_30d — delinquent, borrowing frozen
//   30_60d — formal demand notice stage
//   60_90d — bureau reporting stage
//   default — day 90+, recommend-for-default stage
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import type { Database } from '@/lib/database.types';
import type { Ngwee } from '@/lib/types';

type LoanStatus = Database['public']['Enums']['loan_status'];
type CollectionOutcome = Database['public']['Enums']['collection_outcome'];
type CollectionChannel = Database['public']['Enums']['collection_channel'];

export type Bucket = '0_14d' | '14_30d' | '30_60d' | '60_90d' | 'default';

export interface CollectionsRow {
  loan_id: string;
  loan_number: string;
  borrower_id: string;
  borrower_name: string | null;
  borrower_phone: string;
  status: LoanStatus;
  days_overdue: number;
  outstanding_ngwee: Ngwee;
  last_contact_at: string | null;
  last_contact_channel: CollectionChannel | null;
  last_contact_outcome: CollectionOutcome | null;
  active_ptp_date: string | null;
  active_ptp_amount_ngwee: Ngwee | null;
  bucket: Bucket; // derived
}

export interface CollectionsFilter {
  bucket?: Bucket;
}

export function bucketFor(daysOverdue: number): Bucket {
  if (daysOverdue >= 90) return 'default';
  if (daysOverdue >= 60) return '60_90d';
  if (daysOverdue >= 30) return '30_60d';
  if (daysOverdue >= 14) return '14_30d';
  return '0_14d';
}

// Half-open range [min, max) used for server-side filtering when a bucket
// is selected. `max === null` means "unbounded upper" (default bucket).
function rangeFor(bucket: Bucket): { min: number; max: number | null } {
  switch (bucket) {
    case '0_14d':
      return { min: 0, max: 14 };
    case '14_30d':
      return { min: 14, max: 30 };
    case '30_60d':
      return { min: 30, max: 60 };
    case '60_90d':
      return { min: 60, max: 90 };
    case 'default':
      return { min: 90, max: null };
  }
}

export function useCollectionsQueue(filter: CollectionsFilter = {}) {
  return useQuery({
    queryKey: ['collections-queue', filter],
    queryFn: async (): Promise<CollectionsRow[]> => {
      let q = supabase
        .from('collections_queue')
        .select(
          'loan_id, loan_number, borrower_id, borrower_name, borrower_phone, status, days_overdue, outstanding_ngwee, last_contact_at, last_contact_channel, last_contact_outcome, active_ptp_date, active_ptp_amount_ngwee',
        );
      if (filter.bucket) {
        const r = rangeFor(filter.bucket);
        q = q.gte('days_overdue', r.min);
        if (r.max !== null) q = q.lt('days_overdue', r.max);
      }
      const { data, error } = await q.order('days_overdue', { ascending: false }).limit(200);
      if (error) throw error;
      const rows = (data ?? []) as unknown as Omit<CollectionsRow, 'bucket'>[];
      return rows.map((r) => ({ ...r, bucket: bucketFor(r.days_overdue) }));
    },
  });
}
